import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import CameraGrid from "../../../components/video/CameraGrid";
import { useAcList, useAcSummary } from "../api/accessControlApi";
import {
  CAMERA_ICON,
  CAMERA_TONE,
  ConsolePage,
  KpiRow,
  KpiTile,
  LoadState,
  Panel,
  SEVERITY_TONE,
  ALERT_STATUS_TONE,
  StatusChip,
  ToolButton,
  relativeTime,
  toneOf,
  useTicker,
} from "../components/console/Console";
import { toTileSource } from "./CameraWall";
import { NUVEX_ACCESS_BASE } from "../paths";

// ===========================================================================
// Seguridad y vigilancia → Tablero: KPIs reales del API, muro de 4 cámaras,
// alertas por día, alertas abiertas y cámaras sin conexión.
// ===========================================================================

export const chartTick = { fill: "rgb(var(--c-content-muted))", fontSize: 11 };
export const chartGrid = "rgb(var(--c-border))";
export const chartTooltip = {
  contentStyle: { background: "rgb(var(--c-card))", border: "1px solid rgb(var(--c-border))", borderRadius: 8, color: "rgb(var(--c-content))", fontSize: 12 },
  labelStyle: { color: "rgb(var(--c-content))" },
  cursor: { fill: "rgb(var(--c-subtle))" },
};

export default function SecurityDashboard() {
  const { t, i18n } = useTranslation("accessConsole");
  const navigate = useNavigate();
  const now = useTicker();
  const summaryQ = useAcSummary({ refetchInterval: 30_000 });
  const camerasQ = useAcList("cameras");
  const alertsQ = useAcList("alerts", { status: "open,acknowledged" }, { refetchInterval: 30_000 });
  const s = summaryQ.data;

  const cameras = useMemo(() => camerasQ.data ?? [], [camerasQ.data]);
  const alertCams = new Set((alertsQ.data ?? []).map((a) => a.camera_id));
  const wall = useMemo(() => {
    const live = cameras.filter((c) => c.status !== "offline");
    const prio = [...live.filter((c) => alertCams.has(c.id)), ...live.filter((c) => !alertCams.has(c.id))];
    return prio.slice(0, 4).map(toTileSource);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameras, alertsQ.data]);
  const offline = cameras.filter((c) => c.status === "offline" || c.status === "maintenance");

  const daily = (s?.alerts.daily ?? []).map((d) => ({
    name: new Intl.DateTimeFormat(i18n.language, { weekday: "short" }).format(new Date(`${d.day}T12:00:00Z`)),
    [t("security.chartAlerts")]: d.count - d.critical,
    [t("security.chartCritical")]: d.critical,
  }));

  return (
    <ConsolePage
      icon="ri-shield-check-line"
      title={t("security.title")}
      subtitle={t("security.subtitle")}
      actions={
        <>
          <ToolButton icon={CAMERA_ICON} onClick={() => navigate(`${NUVEX_ACCESS_BASE}/seguridad-y-vigilancia/camaras`)}>{t("security.openWall")}</ToolButton>
          <ToolButton icon="ri-alarm-warning-line" variant="primary" onClick={() => navigate(`${NUVEX_ACCESS_BASE}/seguridad-y-vigilancia/alertas`)}>{t("security.openAlerts")}</ToolButton>
        </>
      }
    >
      <LoadState loading={summaryQ.isLoading} error={summaryQ.error} empty={false} onRetry={() => summaryQ.refetch()}>
        {s && (
          <KpiRow>
            <KpiTile icon={CAMERA_ICON} tone="success" label={t("kpi.camerasOnline")} value={`${s.cameras.online} / ${s.cameras.total}`} hint={t("kpi.offlineN", { interpolation: { escapeValue: false }, count: s.cameras.offline })} />
            <KpiTile icon="ri-alarm-warning-line" tone="error" label={t("kpi.openAlerts")} value={s.alerts.open} hint={t("kpi.criticalN", { interpolation: { escapeValue: false }, count: s.alerts.critical })} />
            <KpiTile icon="ri-login-circle-line" tone="primary" label={t("kpi.events24h")} value={s.events.last24h.toLocaleString(i18n.language)} />
            <KpiTile icon="ri-shield-cross-line" tone="warning" label={t("kpi.denied24h")} value={s.events.denied24h} />
            <KpiTile icon="ri-door-lock-line" tone="info" label={t("kpi.accessPoints")} value={s.points.total} hint={t("kpi.inAlarmN", { interpolation: { escapeValue: false }, count: (s.points.byStatus.alarm ?? 0) + (s.points.byStatus.offline ?? 0) })} />
            <KpiTile icon="ri-map-pin-2-line" tone="neutral" label={t("kpi.sites")} value={s.sites} />
          </KpiRow>
        )}
      </LoadState>

      <div className="grid gap-4 xl:grid-cols-3">
        <Panel className="xl:col-span-2" title={t("security.liveWall")} icon="ri-live-line" bodyClassName="p-1 bg-black">
          <div className="aspect-video w-full">
            <CameraGrid className="h-full w-full" layout={4} slots={wall} onToggleMaximize={() => navigate(`${NUVEX_ACCESS_BASE}/seguridad-y-vigilancia/camaras`)} />
          </div>
        </Panel>
        <Panel title={t("security.openAlertsList")} icon="ri-alarm-warning-line" bodyClassName="max-h-[420px] overflow-y-auto">
          <LoadState loading={alertsQ.isLoading} error={alertsQ.error} empty={!alertsQ.data?.length} emptyText={t("alerts.empty")}>
            <ul className="divide-y divide-line">
              {(alertsQ.data ?? []).slice(0, 8).map((a) => (
                <li key={a.id} className="flex gap-3 px-3 py-2.5">
                  {a.snapshot_url && <img src={a.snapshot_url.replace("/camaras/", "/camaras/thumbs/")} alt="" className="h-12 w-20 shrink-0 rounded object-cover" />}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-content">{a.title}</p>
                    <p className="truncate text-xs text-content-secondary">{a.camera_name ?? a.site_name} · {relativeTime(a.occurred_at, i18n.language, now)}</p>
                    <div className="mt-1 flex gap-1">
                      <StatusChip tone={toneOf(SEVERITY_TONE, a.severity)}>{t(`severity.${a.severity}`)}</StatusChip>
                      <StatusChip tone={toneOf(ALERT_STATUS_TONE, a.status)}>{t(`alertStatus.${a.status}`)}</StatusChip>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </LoadState>
        </Panel>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title={t("security.alertsPerDay")} icon="ri-bar-chart-2-line">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily}>
                <CartesianGrid strokeDasharray="3 3" stroke={chartGrid} vertical={false} />
                <XAxis dataKey="name" tick={chartTick} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={chartTick} axisLine={false} tickLine={false} width={28} />
                <Tooltip {...chartTooltip} />
                <Bar dataKey={t("security.chartAlerts")} stackId="a" fill="rgb(var(--c-warning))" radius={[0, 0, 0, 0]} />
                <Bar dataKey={t("security.chartCritical")} stackId="a" fill="rgb(var(--c-error))" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Panel>
        <Panel title={t("security.camerasDown")} icon="ri-wifi-off-line" bodyClassName="max-h-64 overflow-y-auto">
          <LoadState loading={camerasQ.isLoading} error={camerasQ.error} empty={!offline.length} emptyText={t("security.allCamerasUp")}>
            <ul className="divide-y divide-line">
              {offline.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                  <i className={`${CAMERA_ICON} text-content-muted`} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm text-content">{c.name}</p>
                    <p className="truncate text-xs text-content-secondary">{t("fields.lastSeen")}: {relativeTime(c.last_seen_at, i18n.language, now)}</p>
                  </div>
                  <StatusChip tone={toneOf(CAMERA_TONE, c.status)}>{t(`video.state.${c.status}`)}</StatusChip>
                </li>
              ))}
            </ul>
          </LoadState>
        </Panel>
      </div>
    </ConsolePage>
  );
}
